import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Sparkles, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';
import QuestionnaireSteps from '@/components/estimation/QuestionnaireSteps';
import EstimationResult from '@/components/estimation/EstimationResult';
import Button from '@/components/ui/Button';
import { Platform, RevenueModel, ProjectDescription, SelectedFunctionality } from '@/types/estimation';

interface QuestionnaireData {
  platforms: Platform[];
  revenueModels: RevenueModel[];
  projectDescription: ProjectDescription;
  selectedFunctionalities: SelectedFunctionality[];
}

const EstimationPage = () => {
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleQuestionnaireComplete = (data: QuestionnaireData) => {
    setQuestionnaireData(data);
    setShowResult(true);
    // Scroll to result
    setTimeout(() => {
      const resultSection = document.getElementById('estimation-result');
      if (resultSection) {
        resultSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleReset = () => {
    setQuestionnaireData(null);
    setShowResult(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title="Estimation de projet personnalisée | Orcsys"
        description="Obtenez une estimation détaillée et personnalisée pour votre projet numérique. Répondez à quelques questions et recevez un devis complet avec fonctionnalités, technologies et planning."
        keywords="estimation projet, devis personnalisé, calcul prix développement, estimation application, devis web mobile"
        url="https://innov.orcsys.fr/estimation"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orcsys-50">
        {/* Hero Section */}
        {!showResult && (
          <section className="relative pt-32 pb-20 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-br from-white to-slate-50" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />
            
            {/* Dotted pattern */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: 'radial-gradient(#2b825b 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }} />

            <div className="container px-4 mx-auto relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orcsys-100 mb-6">
                    <Calculator className="w-10 h-10 text-orcsys-500" />
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                    Estimation <span className="text-orcsys-500">personnalisée</span> de votre projet
                  </h1>
                  
                  <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                    Répondez à quelques questions simples et obtenez une estimation détaillée 
                    avec les fonctionnalités, technologies, planning et prix de votre projet.
                  </p>

                  <div className="flex flex-wrap justify-center gap-4 mb-8">
                    <div className="flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-sm">
                      <Sparkles className="w-4 h-4 text-orcsys-500 mr-2" />
                      <span className="text-sm font-medium text-slate-700">100% personnalisé</span>
                    </div>
                    <div className="flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-sm">
                      <ArrowRight className="w-4 h-4 text-orcsys-500 mr-2" />
                      <span className="text-sm font-medium text-slate-700">Gratuit et sans engagement</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* Questionnaire or Result */}
        <section className="py-12 pb-20">
          <div className="container px-4 mx-auto">
            {!showResult ? (
              <QuestionnaireSteps onComplete={handleQuestionnaireComplete} />
            ) : (
              <div id="estimation-result">
                <EstimationResult
                  initialData={questionnaireData}
                  onReset={handleReset}
                />
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        {showResult && (
          <section className="py-20 bg-gradient-to-br from-orcsys-500 to-orcsys-600">
            <div className="container px-4 mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Prêt à concrétiser votre projet ?
                </h2>
                <p className="text-lg text-white/90 mb-8">
                  Contactez-nous pour discuter de votre projet et obtenir un devis détaillé.
                  Nous vous répondrons sous 24h ouvrées.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button.Link
                    href="/contact"
                    className="bg-white text-orcsys-600 hover:bg-white/90 px-8 py-3 text-lg"
                  >
                    Nous contacter
                  </Button.Link>
                  <Button.Link
                    href="/booking"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg"
                  >
                    Prendre rendez-vous
                  </Button.Link>
                </div>
              </motion.div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default EstimationPage;

